/**
 * KPI CARD - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 *
 * Reusable KPI card component for displaying key metrics
 * Used in Dashboard, Finance, Health, and other overview pages
 */

import React from 'react';
import { Panel } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

interface KPICardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  subtitle?: string;
  details?: Array<{ label: string; value: string | number }>;
  loading?: boolean;
  onClick?: () => void;
}

/**
 * KPI Card Component
 *
 * Displays a key performance indicator with:
 * - Title (uppercase, secondary color)
 * - Value (large, bold, primary color)
 * - Icon (in accent-colored circle)
 * - Optional subtitle
 * - Optional detail rows
 * - Optional loading state
 * - Optional click handler (makes it interactive)
 *
 * @example
 * <KPICard
 *   title="Net Worth"
 *   value="$125,430"
 *   icon={ChartBarIcon}
 *   details={[
 *     { label: 'Assets', value: '$150,000' },
 *     { label: 'Liabilities', value: '$24,570' }
 *   ]}
 * />
 */
export function KPICard({
  title,
  value,
  icon: Icon,
  subtitle,
  details,
  loading = false,
  onClick,
}: KPICardProps) {
  const content = (
    <Panel style={onClick ? { cursor: 'pointer' } : undefined}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1 }}>
          {/* Title */}
          <div
            style={{
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.small,
              fontWeight: '600',
              color: COLORS.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: SPACING[8],
            }}
          >
            {title}
          </div>

          {/* Value with Loading State */}
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
              <ArrowPathIcon
                style={{
                  width: '20px',
                  height: '20px',
                  color: COLORS.text.tertiary,
                  animation: 'spin 1s linear infinite',
                }}
              />
              <span
                style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.headings.sizes.h2,
                  color: COLORS.text.tertiary,
                }}
              >
                ...
              </span>
            </div>
          ) : (
            <div
              style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h1,
                fontWeight: '700',
                color: COLORS.text.primary,
                marginBottom: details || subtitle ? SPACING[12] : 0,
              }}
            >
              {value}
            </div>
          )}

          {/* Subtitle */}
          {subtitle && !loading && (
            <div
              style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.tiny,
                color: COLORS.text.secondary,
                marginTop: SPACING[8],
              }}
            >
              {subtitle}
            </div>
          )}

          {/* Detail Rows */}
          {details && details.length > 0 && !loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[4] }}>
              {details.map((detail, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.tiny,
                    color: COLORS.text.secondary,
                  }}
                >
                  <span>{detail.label}:</span>
                  <span style={{ fontWeight: '500' }}>{detail.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Icon */}
        <div
          style={{
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderRadius: RADIUS,
            flexShrink: 0,
          }}
        >
          <Icon style={{ width: '24px', height: '24px', color: COLORS.accent.default }} />
        </div>
      </div>
    </Panel>
  );

  // If onClick is provided, wrap in a clickable container
  if (onClick) {
    return (
      <div
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        role="button"
        tabIndex={0}
        style={{
          textDecoration: 'none',
          display: 'block',
          transition: 'transform 0.1s ease',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
      >
        {content}
      </div>
    );
  }

  return content;
}
